import React, { useEffect, useMemo, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import { Waves, Clock, MapPin, Award, Lock, X } from 'lucide-react'
import { WORLD_MAP_RINGS } from '../data/worldMap'

const RESOLVE_PASSPORT_URL = 'https://jgqwdvcvwkkltakthguh.supabase.co/functions/v1/resolve-passport'

const PAGE_BG = '#F7FAFE'

// Ported verbatim from the app card's design tokens
// (CurrentsUI/Sources/CurrentsUI/Tokens/Colors.swift), light-mode values —
// currents-landing never toggles the `dark` class, so this card matches the
// app's light appearance exactly.
const T = {
  cardTop: '#DCE8F8', // brand.blue.tint
  cardBottom: '#F2F6FC', // brand.blue.wash
  ink: '#14213D', // fg.primary
  fg2: 'rgba(20,33,61,0.58)', // fg.secondary
  fg3: 'rgba(20,33,61,0.34)', // fg.tertiary
  hair: 'rgba(20,33,61,0.06)', // line.hair
  elevated: '#FFFFFF', // bg.elevated
  landFill: '#C3CDD6',
  landStroke: '#7C8A99',
  brandBlue: '#2E6FD9',
}

// The app card renders everything in SF Pro Rounded (`.rounded`). `ui-rounded`
// maps to exactly that on Apple platforms (where a shared passport is almost
// always opened), with graceful fallbacks elsewhere.
const ROUNDED = "ui-rounded, 'SF Pro Rounded', system-ui, -apple-system, sans-serif"
const MONO = "ui-monospace, 'SF Mono', Menlo, monospace"

interface CertificationEntry {
  title: string
  agency: string | null
  levelLabel: string | null
  issuedDate: string | null
  cardImageUrls: string[]
}

interface SiteDot {
  lat: number
  lng: number
  weight: number
}

interface PassportData {
  userId: string
  diverName: string
  diveCountLabel: string
  bottomTimeLabel: string
  siteCountLabel: string
  countriesLabel: string
  deepestLabel: string
  yearsDivingLabel: string
  topCertLabel?: string
  lastDiveLabel?: string
  memberSinceLabel?: string
  siteDots?: SiteDot[]
  hasValidKey?: boolean
  keyExpiresAt?: string
  certifications?: CertificationEntry[]
}

type LoadState =
  | { status: 'loading' }
  | { status: 'notFound' }
  | { status: 'private' }
  | { status: 'error' }
  | { status: 'ready'; data: PassportData }

const PassportPage: React.FC = () => {
  const { handle = '' } = useParams<'handle'>()
  const [searchParams] = useSearchParams()
  const key = searchParams.get('k')
  const [state, setState] = useState<LoadState>({ status: 'loading' })

  useEffect(() => {
    let cancelled = false
    setState({ status: 'loading' })

    const url = new URL(RESOLVE_PASSPORT_URL)
    url.searchParams.set('handle', handle)
    if (key) url.searchParams.set('k', key)

    fetch(url.toString())
      .then(async (resp) => {
        if (cancelled) return
        if (resp.status === 404) return setState({ status: 'notFound' })
        if (!resp.ok) return setState({ status: 'error' })
        const data = (await resp.json()) as PassportData | { isPrivate: true }
        if ('isPrivate' in data && data.isPrivate) return setState({ status: 'private' })
        setState({ status: 'ready', data: data as PassportData })
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'error' })
      })

    return () => {
      cancelled = true
    }
  }, [handle, key])

  // Paint the page background on <body> too, so overscroll / safe-area
  // rubber-banding shows the same colour instead of a bare rectangle edge.
  useEffect(() => {
    const prev = document.body.style.backgroundColor
    document.body.style.backgroundColor = PAGE_BG
    return () => {
      document.body.style.backgroundColor = prev
    }
  }, [])

  return (
    <div className="min-h-screen flex flex-col" style={{ background: PAGE_BG }}>
      <Helmet>
        <title>Currents Dive Passport</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="theme-color" content={PAGE_BG} />
        {key && (
          <>
            <meta httpEquiv="Cache-Control" content="no-store, private" />
            <meta name="referrer" content="no-referrer" />
          </>
        )}
      </Helmet>

      <main className="flex-1 flex flex-col items-center px-4 py-10 md:py-16" style={{ fontFamily: ROUNDED }}>
        <a href="https://trycurrents.app" className="mb-6 flex items-center gap-2">
          <img src="/currents-logo.png" alt="Currents" className="w-8 h-8 rounded-lg" />
          <span className="text-base font-black tracking-tight" style={{ color: T.brandBlue }}>
            Currents
          </span>
        </a>
        {state.status === 'loading' && <Muted>Loading passport…</Muted>}
        {state.status === 'notFound' && <Muted>This passport link doesn't exist.</Muted>}
        {state.status === 'private' && <Muted>This diver's passport is private.</Muted>}
        {state.status === 'error' && <Muted>Couldn't load this passport right now.</Muted>}
        {state.status === 'ready' && <PassportContent data={state.data} handle={handle} />}
      </main>
    </div>
  )
}

const Muted: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="mt-24 text-sm text-center max-w-sm" style={{ color: T.fg2 }}>{children}</div>
)

type Tab = 'identity' | 'certifications'

const PassportContent: React.FC<{ data: PassportData; handle: string }> = ({ data, handle }) => {
  const [tab, setTab] = useState<Tab>('identity')
  const [lightbox, setLightbox] = useState<string | null>(null)
  const hasKey = !!data.hasValidKey
  const mrz = useMemo(() => buildMrz(data, handle), [data, handle])

  return (
    <div className="w-full max-w-[380px] flex flex-col gap-6">
      {/* The card — VStack(spacing: 0): map header / tab strip / content / MRZ,
          on the [blueTint → blueWash] gradient, 20pt corners, hairline border,
          soft shadow. Matches DivePassportCardView.body exactly. */}
      <div
        style={{
          background: `linear-gradient(180deg, ${T.cardTop}, ${T.cardBottom})`,
          borderRadius: 20,
          border: `1px solid ${T.hair}`,
          boxShadow: '0 6px 14px rgba(0,0,0,0.08)',
          overflow: 'hidden',
        }}
      >
        {/* Map header */}
        <div style={{ paddingTop: 14 }}>
          <div style={{ padding: '0 16px', fontSize: 10, fontWeight: 700, letterSpacing: 1.2, color: T.fg2 }}>
            CURRENTS · DIVER PASSPORT
          </div>
          <div style={{ padding: '6px 16px 14px' }}>
            <div style={{ position: 'relative', height: 116, borderRadius: 12, overflow: 'hidden' }}>
              <WorldMap />
              <SiteDots dots={data.siteDots ?? []} />
            </div>
          </div>
        </div>

        {/* Tab strip */}
        <div style={{ display: 'flex', gap: 8, padding: '14px 16px 0', borderBottom: `1px solid ${T.hair}` }}>
          {(['identity', 'certifications'] as Tab[]).map((t) => (
            <button key={t} onClick={() => setTab(t)} style={{ flex: 1 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'center' }}>
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: 0.4,
                    textTransform: 'uppercase',
                    color: tab === t ? T.ink : T.fg3,
                  }}
                >
                  {t === 'identity' ? 'Identity' : 'Certifications'}
                </span>
                <span style={{ height: 2, width: '100%', background: tab === t ? T.ink : 'transparent' }} />
              </div>
            </button>
          ))}
        </div>

        {/* Tab content (padding 16) */}
        <div style={{ padding: 16 }}>
          {tab === 'identity' ? (
            <IdentityTab data={data} />
          ) : (
            <CertificationsTab data={data} hasKey={hasKey} onOpen={setLightbox} />
          )}
        </div>

        {/* MRZ strip */}
        <div style={{ background: T.elevated, padding: '10px 16px' }}>
          <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: 0.5, color: T.fg3, lineHeight: 1.35 }}>
            <div>{mrz[0]}</div>
            <div>{mrz[1]}</div>
          </div>
        </div>
      </div>

      {hasKey && data.keyExpiresAt && <ExpiryCard expiresAt={data.keyExpiresAt} />}

      {!hasKey && <MakeYourOwnCard />}

      {lightbox && <Lightbox url={lightbox} onClose={() => setLightbox(null)} />}
    </div>
  )
}

// The same equirectangular world map the app draws (SiteDotMap): every ring
// filled landFill + stroked landStroke over the card gradient (which shows
// through as ocean, exactly like the app).
const WorldMap: React.FC = () => (
  <svg viewBox="0 0 1000 500" width="100%" height="100%" preserveAspectRatio="none">
    {WORLD_MAP_RINGS.map((ring, i) => (
      <polygon
        key={i}
        points={ring.map(([x, y]) => `${x * 1000},${y * 500}`).join(' ')}
        fill={T.landFill}
        stroke={T.landStroke}
        strokeWidth={0.5}
        vectorEffect="non-scaling-stroke"
      />
    ))}
  </svg>
)

// Dive-site dots plotted over the map with the app's projection and sizing
// (SiteDotMap): x=(lng+180)/360, y=(90-lat)/180; radius 4 + weight/max*7,
// brand.aqua fill with a white ring. Rendered as absolutely-positioned
// circles so they stay round despite the map's non-uniform stretch.
const SiteDots: React.FC<{ dots: SiteDot[] }> = ({ dots }) => {
  if (dots.length === 0) return null
  const maxWeight = Math.max(...dots.map((d) => d.weight), 1)
  return (
    <>
      {dots.map((d, i) => {
        const left = ((d.lng + 180) / 360) * 100
        const top = ((90 - d.lat) / 180) * 100
        const diameter = 8 + (d.weight / maxWeight) * 14
        return (
          <div
            key={i}
            style={{
              position: 'absolute',
              left: `${left}%`,
              top: `${top}%`,
              width: diameter,
              height: diameter,
              transform: 'translate(-50%, -50%)',
              borderRadius: '50%',
              background: '#5BC0E8', // brand.aqua
              border: '1.5px solid #FFFFFF',
              boxSizing: 'border-box',
            }}
          />
        )
      })}
    </>
  )
}

// A live countdown of how long the certification cards stay visible before
// the capability key expires. Shown only on a valid-key view. Ticks every
// second; once it hits zero the cards are gone on the next reload, so this
// tells the operator to look now.
const ExpiryCard: React.FC<{ expiresAt: string }> = ({ expiresAt }) => {
  const target = useMemo(() => new Date(expiresAt).getTime(), [expiresAt])
  const [remaining, setRemaining] = useState(() => Math.max(0, target - Date.now()))

  useEffect(() => {
    const id = setInterval(() => setRemaining(Math.max(0, target - Date.now())), 1000)
    return () => clearInterval(id)
  }, [target])

  const expired = remaining <= 0
  const totalSec = Math.floor(remaining / 1000)
  const mm = Math.floor(totalSec / 60)
  const ss = totalSec % 60
  const clock = `${mm}:${ss.toString().padStart(2, '0')}`

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        padding: '10px 14px',
        borderRadius: 12,
        background: T.elevated,
        border: `1px solid ${T.hair}`,
      }}
    >
      <span
        style={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          background: expired ? '#C16E5C' : '#5C9C7F', // danger / success
          flexShrink: 0,
        }}
      />
      <span style={{ fontSize: 12, fontWeight: 500, color: T.fg2 }}>
        {expired ? (
          'This share link has expired — ask for a new one.'
        ) : (
          <>
            Certification cards visible for{' '}
            <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: T.ink }}>{clock}</span>
          </>
        )}
      </span>
    </div>
  )
}

// The bottom-of-page invitation. A card (not a bare button) so it reads as
// an offer, not an afterthought. Keeps the guardrails: says "free", never
// "download the app", no urgency or counters.
const MakeYourOwnCard: React.FC = () => (
  <a
    href="https://trycurrents.app"
    style={{
      display: 'block',
      textDecoration: 'none',
      borderRadius: 20,
      overflow: 'hidden',
      border: `1px solid ${T.hair}`,
      background: `linear-gradient(180deg, ${T.cardTop}, ${T.cardBottom})`,
      boxShadow: '0 6px 14px rgba(0,0,0,0.06)',
    }}
  >
    <div style={{ padding: '22px 20px', textAlign: 'center' }}>
      <img src="/currents-logo.png" alt="" style={{ width: 36, height: 36, borderRadius: 9, margin: '0 auto 12px' }} />
      <div style={{ fontSize: 17, fontWeight: 800, color: T.ink, lineHeight: 1.25 }}>
        This is what a Currents passport looks like.
      </div>
      <div style={{ fontSize: 13, fontWeight: 500, color: T.fg2, marginTop: 6, lineHeight: 1.45 }}>
        Log every dive, keep your certifications in one place, and carry a passport like this
        wherever you dive.
      </div>
      <div
        style={{
          display: 'inline-block',
          marginTop: 16,
          background: T.ink,
          color: '#FFFFFF',
          fontWeight: 700,
          fontSize: 14,
          borderRadius: 999,
          padding: '12px 26px',
        }}
      >
        Make your own — free
      </div>
    </div>
  </a>
)

// Full-screen image viewer for a tapped certification polaroid.
const Lightbox: React.FC<{ url: string; onClose: () => void }> = ({ url, onClose }) => (
  <div
    onClick={onClose}
    style={{
      position: 'fixed',
      inset: 0,
      zIndex: 50,
      background: 'rgba(8,12,20,0.92)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 24,
      cursor: 'zoom-out',
    }}
  >
    <button
      onClick={(e) => {
        e.stopPropagation()
        onClose()
      }}
      aria-label="Close"
      style={{
        position: 'fixed',
        top: 'max(16px, env(safe-area-inset-top))',
        right: 16,
        width: 40,
        height: 40,
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.14)',
        color: '#FFFFFF',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        border: 'none',
      }}
    >
      <X size={20} strokeWidth={2.5} />
    </button>
    <img
      src={url}
      alt="Certification"
      onClick={(e) => e.stopPropagation()}
      style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 8, boxShadow: '0 12px 40px rgba(0,0,0,0.5)' }}
    />
  </div>
)

const IdentityTab: React.FC<{ data: PassportData }> = ({ data }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: T.fg3 }}>
        Diver
      </div>
      <div style={{ fontSize: 17, fontWeight: 800, textTransform: 'uppercase', color: T.ink }}>
        {data.diverName}
      </div>
      {data.topCertLabel && (
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            marginTop: 8,
            fontSize: 11,
            fontWeight: 600,
            padding: '5px 10px',
            borderRadius: 999,
            background: T.elevated,
            color: T.ink,
          }}
        >
          <Award size={12} strokeWidth={2.5} />
          {data.topCertLabel}
        </div>
      )}
    </div>

    <StatRow
      items={[
        { icon: <Waves size={9} strokeWidth={2.5} />, value: data.diveCountLabel, label: 'Dives logged' },
        { icon: <Clock size={9} strokeWidth={2.5} />, value: data.bottomTimeLabel, label: 'Bottom time' },
        { icon: <MapPin size={9} strokeWidth={2.5} />, value: data.siteCountLabel, label: 'Dive sites' },
      ]}
    />

    <Rule />

    {/* Last dive — the app card's HStack: label left, "date · site" right. */}
    {data.lastDiveLabel && (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: T.fg3 }}>
          Last dive
        </span>
        <span style={{ fontSize: 13, fontWeight: 600, color: T.ink, textAlign: 'right' }}>{data.lastDiveLabel}</span>
      </div>
    )}

    {/* Member since — right-aligned tertiary line, exactly like the app card. */}
    {data.memberSinceLabel && (
      <>
        <Rule />
        <div style={{ textAlign: 'right', fontSize: 11, fontWeight: 500, color: T.fg3 }}>{data.memberSinceLabel}</div>
      </>
    )}
  </div>
)

// statColumn: VStack(alignment: .leading, spacing: 3) — value size 18 heavy,
// then an icon+label row size 9 bold tracking 0.4, tertiary.
const StatRow: React.FC<{ items: { icon: React.ReactNode; value: string; label: string }[] }> = ({ items }) => (
  <div style={{ display: 'flex' }}>
    {items.map((it) => (
      <div key={it.label} style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'flex-start' }}>
        <div style={{ fontSize: 18, fontWeight: 800, color: T.ink }}>{it.value}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 3, color: T.fg3 }}>
          {it.icon}
          <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase' }}>{it.label}</span>
        </div>
      </div>
    ))}
  </div>
)

const Rule: React.FC = () => <div style={{ height: 1, background: T.hair }} />

const CertificationsTab: React.FC<{ data: PassportData; hasKey: boolean; onOpen: (url: string) => void }> = ({
  data,
  hasKey,
  onOpen,
}) => {
  const certs = data.certifications ?? []

  if (!hasKey) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '24px 0' }}>
        <Lock size={16} strokeWidth={2} color={T.fg3} />
        <p style={{ fontSize: 13, fontWeight: 500, color: T.fg2 }}>Certification cards are private.</p>
      </div>
    )
  }

  if (certs.length === 0) {
    return (
      <p style={{ fontSize: 13, fontWeight: 500, textAlign: 'center', padding: '20px 0', color: T.fg2 }}>
        No certifications logged yet.
      </p>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {certs.map((cert, i) => (
        // One row per cert: polaroid on the left, info on the right.
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {cert.cardImageUrls.length > 0 && (
            <Polaroid url={cert.cardImageUrls[0]} onOpen={() => onOpen(cert.cardImageUrls[0])} />
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: T.ink }}>{cert.title}</div>
            <div style={{ fontSize: 11, fontWeight: 500, color: T.fg2 }}>
              {[cert.agency, cert.issuedDate].filter(Boolean).join(' · ')}
            </div>
            {cert.levelLabel && (
              <div style={{ marginTop: 3, fontSize: 9, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', color: T.fg3 }}>
                {cert.levelLabel}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

// A physical polaroid of the uploaded certification photo — white frame with
// a wider bottom lip and slight tilt, tap to open full-screen. Mirrors the
// "physical object" treatment the app gives cert cards. The cert title/agency
// sit to the right of it in the row, so the frame carries no caption.
const Polaroid: React.FC<{ url: string; onOpen: () => void }> = ({ url, onOpen }) => (
  <button
    onClick={onOpen}
    style={{
      flexShrink: 0,
      background: '#FFFFFF',
      padding: '6px 6px 12px',
      borderRadius: 4,
      border: `1px solid ${T.hair}`,
      boxShadow: '0 4px 12px rgba(20,33,61,0.12)',
      transform: 'rotate(-2deg)',
      cursor: 'zoom-in',
      display: 'block',
    }}
  >
    <img
      src={url}
      alt="Certification card"
      style={{ width: 96, height: 72, objectFit: 'cover', borderRadius: 2, display: 'block' }}
    />
  </button>
)

// Decorative MRZ strip, matching DivePassportViewModel.mrzLines — no policy
// number on the public page (insurance stays local-only, never sent to the
// server), so the handle fills that slot.
function buildMrz(data: PassportData, handle: string): [string, string] {
  const pad = (s: string) => s.padEnd(44, '<').slice(0, 44)
  const parts = data.diverName.toUpperCase().split(' ')
  const last = parts[parts.length - 1] || 'DIVER'
  const first = parts.slice(0, -1).join('<')
  const dives = (parseInt(data.diveCountLabel, 10) || 0).toString().padStart(4, '0')
  const line1 = pad(`PDIVE<<${last}<${first}<<${dives}DIVES<<<<<`)
  const cert = (data.topCertLabel ?? 'DIVER').replace(/\s+/g, '').toUpperCase()
  const line2 = pad(`${handle.toUpperCase()}<CURRENTS<<${cert}<CURRENTS.`)
  return [line1, line2]
}

export default PassportPage
